import React, { useEffect, useState } from "react";
import { Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    AddNewSiteContacts,
    deletePaceSiteContact,
    editPaceSiteContact,
    getAllPaceSiteContacts
} from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { confirmDialog } from "../../../components/ConfirmDialog/confirmDialog";

const PaceSiteContact = () => {
    const {state} = useAuth();
    const [paceContacts, setPaceContacts] = useState<any>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [currentContact, setCurrentContact] = useState<any>(null);
    const [contactName, setContactName] = useState('');
    const [formError, setFormError] = useState('');
    const [, setOpen] = useState(false);

    const handleAddClick = () => {
        setModalMode('add');
        setContactName('');
        setCurrentContact(null);
        setFormError('');
        setIsDialogOpen(true);
    };

    const handleEditClick = (params: any) => {
        setModalMode('edit');
        setCurrentContact(params.row);
        setContactName(params.row.contact);
        setFormError('');
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setContactName('');
        setCurrentContact(null);
        setFormError('');
    };

    const handleSubmit = async () => {
        if (!contactName.trim()) {
            setFormError('Pace Site Contact cannot be empty');
            return;
        }

        if (modalMode === 'add') {
            const response = await AddNewSiteContacts(state.user?.token, contactName);
            if (response) {
                toast.success(response.data);
                getAllPaceSiteContacts(state.user?.token).then(setPaceContacts);
                handleDialogClose();
            } else {
                toast.error('Something went wrong!');
            }
        } else {
            if (currentContact) {
                const response = await editPaceSiteContact(state.user?.token, currentContact.id, contactName);
                if (response) {
                    toast.success(response.data);
                    getAllPaceSiteContacts(state.user?.token).then(setPaceContacts);
                    handleDialogClose();
                } else {
                    toast.error('Something went wrong!');
                }
            }
        }
    };

    const handleDelete = async (id: any) => {
        const response = await deletePaceSiteContact(state.user?.token, id);
        if(response) {
            toast.success(response.data);
            getAllPaceSiteContacts(state.user?.token).then(setPaceContacts);
        } else {
            toast.error('Something went wrong!')
        }
    };

    const columns: any = [
        {
            field: "contact",
            headerName: "Pace Site Contact",
            flex: 1,
            renderCell: (params: any) => (
                <div>{params.row.contact}</div>
            ),
        },
        {
            field: "createdDate",
            headerName: "Created Date",
            flex: 0.3,
            renderCell: (params: any) => (
                <div>
                    {format(new Date(params.row.createdDate || Date.now()), 'dd/MM/yyyy')}
                </div>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            headerAlign: "center",
            align: "center",
            renderCell: (params: any) => (
                <div>
                    <Button 
                        color="primary"
                        variant="contained" 
                        onClick={() => handleEditClick(params)}
                        size="small"
                        style={{ marginRight: 8, backgroundColor: "#6c757d", boxShadow: "none" }}
                    >
                        Edit
                    </Button>  
                    <Button 
                        color="error" 
                        variant="contained"
                        size="small"
                        style={{boxShadow: "none"}}
                        onClick={() => {
                            setOpen(true);
                            confirmDialog({
                                title: 'Are you sure you want to delete this Pace Site Contact?',
                                onOk: () => handleDelete(params.id),
                                okButtonProps: { danger: true },
                                okText: 'YES',
                                cancelText: 'NO',
                                centered: true
                            });
                        }}
                    >
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    useEffect(() => {
        getAllPaceSiteContacts(state.user?.token).then(setPaceContacts);
    }, []);

    return (
        <Box 
            sx={{ 
                p: 3, 
                borderRadius: '16px',
                backgroundColor: '#ffffff',
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
            <Box
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    alignItems: 'center',
                    m: 3,
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddClick}  
                    style={{boxShadow: 'none'}}
                >
                    Add Pace Site Contact
                </Button>
            </Box>
            <Box 
                sx={{
                    margin: '0 auto',
                    height: '735px',
                    "@media (max-width: 900px)": {
                        overflowX: "auto",
                        margin: 1,
                        minWidth: "95%",
                    },
                }}>
                <DataGrid
                    rows={paceContacts}
                    columns={columns}
                    disableRowSelectionOnClick
                    sx={{
                        "@media (max-width: 700px)": {
                            minWidth: "100%",
                        },
                        "& .MuiDataGrid-columnHeader": {
                            color: "#333",
                            fontWeight: "bold",
                            fontSize: 15,
                            backgroundColor: '#f0f0f0',
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: "bold",
                        },
                        "@media (max-width: 600px)": {
                            fontSize: "0.7rem",
                        },
                    }}
                />
            </Box>

            {/* Material-UI Dialog for Add/Edit */}
            <Dialog 
                open={isDialogOpen} 
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {modalMode === 'add' ? 'Add New Pace Site Contact' : 'Edit Pace Site Contact'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        size="small"
                        margin="dense"
                        label="Pace Site Contact"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={contactName}
                        onChange={(e) => {
                            setContactName(e.target.value);
                            setFormError('');
                        }}
                        error={!!formError}
                        helperText={formError}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSubmit();
                            }
                        }}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" style={{boxShadow: 'none'}}>
                        {modalMode === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PaceSiteContact;
